<template>
  <div class="product-search">
    <div class="container">
      <div class="product-search__content">
        <div v-if="!hideFilters"
             class="product-search__filters">
          <h2 class="product-search__filters-heading">
            Filter products:
          </h2>

          <div class="product-search__filter product-search__filter--grow">
            <div class="product-search__checkboxes">
              <div class="product-search__checkbox">
                <b-form-checkbox-group v-model="appliedFilters"
                                       name="filter">
                  <b-form-checkbox
                    v-for="attribute in sortedFilterAttributes"
                    :key="attribute.id"
                    :value="attribute.id">
                    {{ attribute.name }}
                  </b-form-checkbox>
                </b-form-checkbox-group>
              </div>
            </div>
          </div>

          <div class="product-search__filter">
            <input v-model="searchTerm"
                   placeholder="Search"
                   class="product-search__textbox">
          </div>
        </div>

        <div v-if="loading">
          <div class="product-search__loading">
            <font-awesome-icon icon="spinner"
                               spin></font-awesome-icon>
          </div>
        </div>

        <div v-else
             class="product-search__results">
          <div v-for="result in sortedResults"
               :key="result.url"
               class="product-search__result">
            <ProductSearchResult v-bind="result"></ProductSearchResult>
          </div>

          <div v-if="results.length === 0"
               class="product-search__no-results">
            No results
          </div>
        </div>

        <div class="product-search__pagination">
          <ProductSearchPagination :page-count="pageCount"
                                   :page-index="pageIndex"
                                   @changePage="changePage"></ProductSearchPagination>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { debounce } from "lodash";
import Uri from "urijs";
import ProductSearchResult from "~/Components/Search/ProductSearchResult.vue";
import ProductSearchPagination from "~/Components/Search/ProductSearchPagination.vue";
import { getCategorySortIndex } from "~/Scripts/modules/search";

const uri = new Uri( window.document.location.href );
const queryString = uri.search( true );

const parameters = {
  pageIndex: 0,
  searchTerm: "",
  appliedFilters: []
};

if ( queryString ) {
  if ( queryString.page ) {
    parameters.pageIndex = queryString.page - 1;
  }

  if ( queryString.q ) {
    parameters.searchTerm = queryString.q;
  }

  if ( queryString.filters ) {
    parameters.appliedFilters = queryString.filters.split( "," );
  }
}

export default {
  name: "ProductSearch",
  components: {
    ProductSearchResult,
    ProductSearchPagination
  },
  props: {
    nodeId: {
      type: Number,
      required: true
    },
    parentAttributes: {
      type: Array,
      required: true
    },
    filterAttributes: {
      type: Array,
      required: true
    },
    hideFilters: {
      type: Boolean,
      default: false
    },
    orderedProducts: {
      type: Array,
      default: () => []
    }
  },
  data: () => ( {
    results: [],
    resultsPerPage: 24,
    resultCount: 0,
    loading: true,
    ...parameters
  } ),
  computed: {
    sortedResults () {
      let result = [];

      if ( !this.results || this.results.length <= 0 ) {
        return result;
      }

      const orderedProducts = this.results
        .filter( p => p.metaData && p.metaData.Website_DisplayOrder );

      const otherProducts = this.results
        .filter( p => !p.metaData || !p.metaData.Website_DisplayOrder );

      orderedProducts.sort( ( a, b ) => this.getDisplayOrder( a ) - this.getDisplayOrder( b ) );

      otherProducts.sort( this.sortBy( "name" ) );

      result = [...orderedProducts, ...otherProducts];

      if ( this.isValidArrayWithContents( this.orderedProducts ) === false ) {
        return this.paginate( result, this.pageIndex, this.resultsPerPage );
      }

      /*
      *  Priortise ordered products. Intended for use by the product viewer
      *  property editor from within Umbraco to allow user defined sort orders
      *  for umbraco content.
      */
      const mutableProductsWithoutUmbraco = result.filter(
        product => product.nodeId === -1 || product.nodeId == null
      );

      const mappedUmbracoProducts = this.orderedProducts.map( umbracoProduct => {
        const { nodeId } = umbracoProduct;
        return this.results.find( product => product.nodeId === nodeId );
      } ).filter( mappedProduct => mappedProduct != null );

      return this.paginate(
        [...mappedUmbracoProducts, ...mutableProductsWithoutUmbraco],
        this.pageIndex,
        this.resultsPerPage
      );
    },
    sortedFilterAttributes () {
      const result = [...this.filterAttributes];

      result.sort( ( a, b ) => {
        const ai = getCategorySortIndex( a.name );
        const bi = getCategorySortIndex( b.name );
        return ai - bi;
      } );

      return result;
    },
    pageCount () {
      let result = 0;

      if ( this.resultCount > 0 ) {
        result = Math.ceil( this.resultCount / this.resultsPerPage );
      }

      return result;
    }
  },
  watch: {
    pageIndex ( val ) {
      this.updateQueryString( "page", val + 1 );
      this.fetchData();
    },
    searchTerm ( val ) {
      this.handleSearchTerm( val );
    },
    appliedFilters: {
      deep: true,
      handler ( val ) {
        this.pageIndex = 0;

        const stringified = val.join( "," );
        this.updateQueryString( "filters", stringified );
        this.fetchData();
      }
    }
  },
  created () {
    this.fetchData();
  },
  methods: {
    paginate ( arr, pageNum, resultsPerPage ) {
      return arr.splice( pageNum * resultsPerPage, resultsPerPage );
    },
    fetchData () {
      this.loading = true;

      axios.post( "/umbraco/Surface/ProductSearch/SearchAll", {
        nodeId: this.nodeId,
        parentAttributes: this.parentAttributes,
        filterAttributes: this.appliedFilters,
        searchTerm: this.searchTerm
      } )
        .then( ( { data } ) => {
          this.results = data.results;
          this.resultCount = data.resultCount;
          this.resultsPerPage = 12;
        } )
        .finally( () => {
          this.loading = false;
        } );
    },
    isValidArrayWithContents ( arr ) {
      return Array.isArray( arr ) && arr[0];
    },
    updateQueryString ( key, value ) {
      let newPageUrl = window.location.href;
      const param = {};
      param[key] = value;

      if ( value ) {
        newPageUrl = Uri( newPageUrl ).setQuery( param );
      } else {
        newPageUrl = Uri( newPageUrl ).removeQuery( key );
      }

      window.history.pushState( {}, "", newPageUrl );
    },
    changePage ( val ) {
      this.pageIndex = val;
    },
    getDisplayOrder ( product ) {
      let order = Number.MAX_SAFE_INTEGER;

      if ( product && product.metaData ) {
        const value = product.metaData.Website_DisplayOrder;
        if ( value ) {
          order = Number.parseInt( value, 10 );
        }
      }
      return order;
    },
    handleSearchTerm: debounce( function handleSearchTerm ( val ) {
      this.updateQueryString( "q", val );
      this.fetchData();
    }, 200 ),
    getObjectValue ( obj, k ) {
      const keys = k.split( "." );

      let value = { ...obj };
      for ( let i = 0; keys.length > i; i += 1 ) {
        if ( value && value[keys[i]] ) {
          value = value[keys[i]];
        }
      }

      return value;
    },
    sortBy ( ...args ) {
      const component = this;

      // utility functions
      const defaultCmp = function( a, b ) {
        if ( a === b ) return 0;
        return a < b ? -1 : 1;
      };

      const getCmpFunc = function( primer, reverse ) {
        let cmp = defaultCmp;
        if ( primer ) {
          cmp = function( a, b ) {
            return defaultCmp( primer( a ), primer( b ) );
          };
        }
        if ( reverse ) {
          return function( a, b ) {
            return -1 * cmp( a, b );
          };
        }
        return cmp;
      };

      const fields = [];
      const nFields = args.length;
      let field;
      let name;
      let cmp;

      // preprocess sorting options
      for ( let i = 0; i < nFields; i += 1 ) {
        field = args[i];
        if ( typeof field === "string" ) {
          name = field;
          cmp = defaultCmp;
        } else {
          name = field.name;
          cmp = getCmpFunc( field.primer, field.reverse );
        }

        fields.push( {
          name,
          cmp
        } );
      }

      return function( A, B ) {
        let key;
        let compare;
        let result;

        for ( let i = 0, l = nFields; i < l; i += 1 ) {
          result = 0;
          field = fields[i];
          key = field.name;
          compare = field.cmp;

          result = compare( component.getObjectValue( A, key ),
            component.getObjectValue( B, key ) );

          if ( result !== 0 ) break;
        }

        return result;
      };
    }
  }
};
</script>

<style lang="less" scoped>
@import "../../Styles/utils/colors.less";

.product-search {
  margin: 30px 0;

  &__content {
    background: #e4f1f2;
    padding: 30px;
    padding-bottom: 15px;

    @media ( max-width: 991px ) {
      padding: 15px 15px 0;
    }
  }

  &__filters {
    background: #fff;
    padding: 15px;
    margin-bottom: 15px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    @media ( max-width: 991px ) {
      flex-direction: column;
      align-items: flex-start;
    }
  }

  &__filters-heading {
    color: @color-accent-6;
    font-weight: 600;
    margin-right: 15px;
    font-size: 14px;
    margin-bottom: 0;
    flex-shrink: 0;

    @media ( max-width: 991px ) {
      margin-bottom: 15px;
    }
  }

  &__filter {
    &--grow {
      flex-grow: 1;
    }

    @media ( max-width: 991px ) {
      width: 100%;
      margin-bottom: 15px;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  &__results {
    display: flex;
    flex-wrap: wrap;
  }

  &__result {
    flex-basis: calc( 50% - 7.5px );
    flex-grow: 0;
    flex-shrink: 0;
    display: flex;
    margin-bottom: 15px;

    &:nth-child( odd ) {
      margin-right: 7.5px;
    }

    &:nth-child( even ) {
      margin-left: 7.5px;
    }

    @media ( max-width: 991px ) {
      flex-basis: 100%;
      margin-left: 0 !important;
      margin-right: 0 !important;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  &__no-results {
    text-align: center;
    font-weight: 600;
    color: #006a7f;
    flex-grow: 1;
    padding-top: 30px;
    padding-bottom: 30px;
    font-size: 0.875rem;
  }

  &__loading {
    text-align: center;
    font-size: 50px;
    color: @color-accent-6;
  }

  &__checkboxes {
    display: flex;
    justify-content: space-between;
  }

  &__checkbox {
    margin-bottom: 0;

    @media ( max-width: 768px ) {
      > div {
        display: flex;
        flex-wrap: wrap;
        width: 100%;
      }

      .custom-checkbox {
        flex-basis: 50%;
        margin-right: 0;
        flex-grow: 1;
      }
    }
  }

  &__textbox {
    border: 2px solid @color-accent-8;
    padding: 2px 5px;
    font-size: 14px;
    width: 100%;

    @media ( min-width: 992px ) {
      width: 220px;
    }
  }
}
</style>
